import { Action, Dispatch as IDispatch, Reducer as IReducer } from 'redux';

export interface IFluxAction<TPayload> extends Action {
  payload?: TPayload;
}

export interface IActionCreator<TPayload1, TPayload2> extends Function {
  (payload: TPayload1): IFluxAction<TPayload2>;
  toString(): string;
}

export interface IAsyncActionCreator<TRequestPayload, TResultPayload> {
  (payload: TRequestPayload): ((dispatch: IDispatch<TResultPayload>) => Promise<TResultPayload>);
  request?: IActionCreator<TRequestPayload, TRequestPayload>;
  result?: IActionCreator<TResultPayload, TResultPayload>;
  error?: IActionCreator<Error, Error>;
}

let actionCreatorIndex = 0;

export function defineActionType(name: string): string {
  return `[${actionCreatorIndex++}] ${name}`;
}

interface ICreateActionCreator {
  <TPayload>(name: string): IActionCreator<TPayload, TPayload>;
  <TPayload1, TPayload2>(name: string, map: (payload: TPayload1) => TPayload2): IActionCreator<TPayload1, TPayload2>;
}

export const createActionCreator: ICreateActionCreator =
function createActionCreator<TPayload1, TPayload2>(name: string, map?: (payload: TPayload1) => TPayload2): IActionCreator<TPayload1, TPayload2> {
  const type = defineActionType(name);

  const actionCreator = (payload: TPayload1) => ({
    type,
    payload: map ? map(payload) : (payload as any),
  });

  actionCreator.toString = () => type;

  return actionCreator;
}

export interface IReducerBuilder<TState> {
  on<TPayload1, TPayload2>(
    actionCreator: IActionCreator<TPayload1, TPayload2>,
    reducer: (state: TState, action: IFluxAction<TPayload2>) => TState
  ): IReducerBuilder<TState>;
  build(defaultState: TState): IReducer<TState>;
}

export function createReducer<TState>(): IReducerBuilder<TState> {
  const reducersMap: {[type: string]: (state: TState, payload: any) => TState} = {};

  return {
    on<TPayload1, TPayload2>(
      actionCreator: IActionCreator<TPayload1, TPayload2>,
      reducer: (state: TState, action: IFluxAction<TPayload2>) => TState
    ): IReducerBuilder<TState> {
      reducersMap[actionCreator.toString()] = reducer;
      return this;
    },
    build(defaultState: TState): IReducer<TState> {
      return (state: TState = defaultState, action: IFluxAction<any>) => {
        if (!(action.type in reducersMap)) {
          return state;
        }

        return reducersMap[action.type](state, action);
      };
    },
  };
}



// WEBPACK FOOTER //
// ./src/utils/fsa.ts